import { Group, Popover, SimpleGrid, Text, UnstyledButton, useMantineTheme, UnstyledButtonProps } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import React from "react";
import { useTranslation } from "react-i18next";
import { IconBase } from "react-icons";
import { MdCheck, MdLanguage, MdUnfoldMore } from "react-icons/md";
import { SUPPORTED_LANGUAGE_CODES } from "@app/i18n";
import { capitalize } from "@app/Utils/Functions";

const LanguageItem = React.forwardRef<
	HTMLButtonElement,
	UnstyledButtonProps & React.HTMLAttributes<HTMLButtonElement> & { langCode: string }
>(function LanguageItem(props, ref) {
	const { i18n } = useTranslation();
	const { langCode, onClick } = props;
	const theme = useMantineTheme();

	const languageName = React.useMemo(
		() => capitalize(new Intl.DisplayNames([langCode, 'en'], { type: "language" }).of(langCode)),
		[langCode]
	);

	const isActive = React.useMemo(
		() => i18n.language.toLowerCase() === langCode.toLowerCase(),
		[i18n.language, langCode]
	);

	return (
		<UnstyledButton
			ref={ref}
			onClick={async (e) => {
				await i18n.changeLanguage(langCode);
				onClick?.(e);
			}}
			sx={{
				color: isActive ? theme.colors.gray[0] : theme.colors.gray[5],
				"&:hover": { color: theme.colors.gray[0] }
			}}
		>
			<Group spacing="xs" align="flex-start">
				{isActive ? <MdCheck color={theme.colors.blue[5]} size={20} /> : <IconBase size={20} />}
				<Text>{languageName}</Text>
			</Group>
		</UnstyledButton>
	);
});

export const LanguageSelector = () => {
	const { i18n } = useTranslation();
	const [opened, { close, toggle }] = useDisclosure(false);
	const theme = useMantineTheme();

	const currentLanguageName = React.useMemo(() => {
		return capitalize(new Intl.DisplayNames([i18n.language, 'en'], { type: "language" }).of(i18n.language));
	}, [i18n.language]);

	return (
		<Popover width={300} opened={opened} onClose={close}>
			<Popover.Target>
				<UnstyledButton
					onClick={toggle}
					sx={{
						color: theme.colors.gray[3],
						"&:hover": { color: theme.colors.gray[0] }
					}}
				>
					<Group spacing="xs" align="center">
						<MdLanguage size={24} color={theme.colors.blue[5]} />
						<Text>{currentLanguageName}</Text>
						<MdUnfoldMore />
					</Group>
				</UnstyledButton>
			</Popover.Target>
			<Popover.Dropdown>
				<SimpleGrid cols={2}>
					{SUPPORTED_LANGUAGE_CODES.map((langCode) => (
						<LanguageItem
							key={langCode}
							langCode={langCode}
							onClick={close}
						/>
					))}
				</SimpleGrid>
			</Popover.Dropdown>
		</Popover>
	);
};